import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Logger } from '@app/@shared';
import { AuthService } from '@app/@shared/services/auth.service';
import {
  AlertController,
  IonInput,
  LoadingController,
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import { NotificationsService } from '@app/@shared/services/notifications.service';
import { TrackingService } from '@app/@shared/services/tracking.service';
import { GeneralService } from '@app/@shared/services/general.service';
import { CustomerService } from '@app/@shared/services/customer.service';
import { BlackUser } from '@app/@shared/models/customer.model';
import { Router } from '@angular/router';
import { MailServiceService } from '@app/@shared/services/mail-service/mail-service.service';
import { CampaignService } from '@app/@shared/services/campaign.service';
import { LegalComponent } from '@app/legal/legal.component';
import { environment } from '@env/environment';
import { HttpService } from '@app/@shared/services/http.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { WalletApiService } from '@app/@shared/services/wallet-api.service';
import { WalletTypeService } from '@app/@shared/services/wallet-type.service';
const log = new Logger('register-user');

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit, OnDestroy {
  @ViewChild('inputName') inputName!: IonInput;
  form!: FormGroup;
  isLoading = false;
  blackUser: BlackUser[] = [];

  isAdult: boolean = false;
  isValidDate: boolean = true;
  @Input() isModal: boolean = false;
  isValidEmailDomain: boolean = true;

  // Propiedades para rastrear los requisitos de contraseña
  passwordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
  };

  // Propiedades para verificación de email
  verificationCode: string = '';
  verificationCodeSent: boolean = false;
  verificationCodeVerified: boolean = false;
  isSendingCode: boolean = false;

  // Propiedades para el contador de reenvío
  resendCountdown: number = 0; // Tiempo restante en segundos
  resendTimer: any = null; // Referencia al intervalo del contador
  readonly RESEND_COOLDOWN_SECONDS = 60; // Tiempo de espera antes de poder reenviar (60 segundos)

  // Propiedades para validación de campos faltantes
  showFieldErrors: boolean = false;
  missingFieldsMessage: string = '';

  constructor(
    private fb: FormBuilder,
    private authS: AuthService,
    private loadingCtrl: LoadingController,
    private translateService: TranslateService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private navCtrl: NavController,
    private notificationsS: NotificationsService,
    private trackingS: TrackingService,
    private generalS: GeneralService,
    private customerS: CustomerService,
    private router: Router,
    public mailSrv: MailServiceService,
    public campaignS: CampaignService,
    private modalCtrl: ModalController,
    private httpS: HttpService,
    private http: HttpClient,
    private walletApiS: WalletApiService,
    private walletTypeS: WalletTypeService
  ) {}

  ngOnInit() {}

  ionViewDidEnter() {
    this.createForm();

    this.form.get('name')?.valueChanges.subscribe((value) => {
      if (value && value !== value.toUpperCase()) {
        this.form.get('name')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.form.get('lastName')?.valueChanges.subscribe((value) => {
      if (value && value !== value.toUpperCase()) {
        this.form.get('lastName')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    this.form.get('slastName')?.valueChanges.subscribe((value) => {
      if (value && value !== value.toUpperCase()) {
        this.form.get('slastName')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    // Suscribirse a cambios en la contraseña para validar requisitos en tiempo real
    this.form.get('password')?.valueChanges.subscribe((value) => {
      this.checkPasswordRequirements(value || '');
    });

    // Suscribirse a cambios en el email para validar dominio
    this.form.get('email')?.valueChanges.subscribe((value) => {
      this.validateEmailDomain(value || '');
      // Resetear verificación si cambia el email
      if (this.verificationCodeSent) {
        this.verificationCodeSent = false;
        this.verificationCodeVerified = false;
        this.verificationCode = '';
        this.form.get('verificationCode')?.setValue('');
        this.form.get('verificationCode')?.setErrors(null);
        this.form.get('verificationCode')?.clearValidators();
        this.form.get('verificationCode')?.updateValueAndValidity();
        // Limpiar el contador si existe
        this.stopResendCountdown();
      }
    });
  }

  createForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      lastName: ['', [Validators.required, Validators.minLength(3)]],
      slastName: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email, this.emailDomainValidator.bind(this)]],
      password: ['', [Validators.required, Validators.minLength(8), this.passwordValidator.bind(this)]],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      termsAccepted: [false, Validators.requiredTrue],
      verificationCode: [''],
    });

    setTimeout(async () => {
      await this.inputName.setFocus();
    }, 800);
  }

  // Validador personalizado para contraseña
  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';

    if (!value) {
      return null; // Dejar que Validators.required maneje el caso vacío
    }

    const errors: ValidationErrors = {};

    // Mínimo 8 caracteres
    if (value.length < 8) {
      errors['minLength'] = true;
    }

    // Al menos una mayúscula
    if (!/[A-Z]/.test(value)) {
      errors['hasUpperCase'] = true;
    }

    // Al menos una minúscula
    if (!/[a-z]/.test(value)) {
      errors['hasLowerCase'] = true;
    }

    // Al menos un número
    if (!/[0-9]/.test(value)) {
      errors['hasNumber'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  // Verificar requisitos de contraseña en tiempo real
  checkPasswordRequirements(password: string) {
    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    };
  }

  // Validador personalizado para dominio de email (no bloquea el registro, solo advierte)
  emailDomainValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value || '';
    if (!email) {
      this.isValidEmailDomain = true;
      return null; // Dejar que Validators.required maneje el caso vacío
    }

    // Validar formato básico de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.isValidEmailDomain = true; // No mostrar advertencia si el formato básico es inválido (ya lo maneja Validators.email)
      return null;
    }

    // Extraer el dominio
    const domain = email.split('@')[1]?.toLowerCase() || '';

    // Extensiones válidas: .com, .mx, .com.mx, .org
    const validExtensions = ['.com', '.mx', '.com.mx', '.org'];
    const hasValidExtension = validExtensions.some((ext) => domain.endsWith(ext));

    // Proveedores de email válidos
    const validProviders = [
      'yahoo',
      'hotmail',
      'gmail',
      'outlook',
      'live',
      'msn',
      'aol',
      'icloud',
      'protonmail',
      'mail',
    ];
    const hasValidProvider = validProviders.some((provider) => domain.includes(provider));

    // El dominio es válido si tiene una extensión válida Y un proveedor válido
    const isValid = hasValidExtension && hasValidProvider;

    this.isValidEmailDomain = isValid;
    return null; // No retornar error para no bloquear el registro
  }

  // Validar dominio del email en tiempo real
  validateEmailDomain(email: string) {
    if (!email) {
      this.isValidEmailDomain = true;
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.isValidEmailDomain = true;
      return;
    }

    const domain = email.split('@')[1]?.toLowerCase() || '';

    // Extensiones válidas: .com, .mx, .com.mx, .org
    const validExtensions = ['.com', '.mx', '.com.mx', '.org'];
    const hasValidExtension = validExtensions.some((ext) => domain.endsWith(ext));

    // Proveedores de email válidos
    const validProviders = [
      'yahoo',
      'hotmail',
      'gmail',
      'outlook',
      'live',
      'msn',
      'aol',
      'icloud',
      'protonmail',
      'mail',
    ];
    const hasValidProvider = validProviders.some((provider) => domain.includes(provider));

    // El dominio es válido si tiene una extensión válida Y un proveedor válido
    this.isValidEmailDomain = hasValidExtension && hasValidProvider;
  }

  // Verificar si todos los requisitos se cumplen
  get isPasswordValid(): boolean {
    return (
      this.passwordRequirements.minLength &&
      this.passwordRequirements.hasUpperCase &&
      this.passwordRequirements.hasLowerCase &&
      this.passwordRequirements.hasNumber
    );
  }

  formatDate(event: any) {
    let input = event.target.value.replace(/\D/g, ''); // Solo números

    // Limitar a 8 caracteres (DDMMAAAA)
    if (input.length > 8) {
      input = input.slice(0, 8);
    }

    let formatted = input;

    // Aplicar formato DD-MM-AAAA
    if (input.length > 6) {
      // DD-MM-AAAA
      formatted = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(4, 8)}`;
    } else if (input.length > 4) {
      // DD-MM-AA
      formatted = `${input.slice(0, 2)}-${input.slice(2, 4)}-${input.slice(4)}`;
    } else if (input.length > 2) {
      // DD-MM
      formatted = `${input.slice(0, 2)}-${input.slice(2)}`;
    }

    // Actualizar el valor del formulario sin emitir evento extra
    this.form.get('dateOfBirth')?.setValue(formatted, { emitEvent: false });

    // Validar cuando se complete la fecha (DD-MM-AAAA = 10 caracteres con guiones)
    if (input.length === 8) {
      this.validateDateAndAge(formatted);
    } else {
      this.isValidDate = true;
      this.isAdult = false;
    }
  }

  validateDateAndAge(value: string) {
    // Validar formato DD-MM-AAAA
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = value.match(regex);

    if (!match) {
      this.isValidDate = false;
      this.isAdult = false;
      return;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validar rangos básicos
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
      this.isValidDate = false;
      this.isAdult = false;
      return;
    }

    const birthDate = new Date(year, month - 1, day);

    // Verificar que la fecha exista (ej. 31-02-2023 → inválida)
    if (birthDate.getFullYear() !== year || birthDate.getMonth() + 1 !== month || birthDate.getDate() !== day) {
      this.isValidDate = false;
      this.isAdult = false;
      return;
    }

    this.isValidDate = true;

    // Calcular edad
    const today = new Date();
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() - (month - 1);
    const dayDiff = today.getDate() - day;

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    this.isAdult = age >= 18;
  }

  // Generar código de verificación aleatorio de 6 dígitos
  generateVerificationCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  // Enviar código de verificación por email
  async sendVerificationCode() {
    const email = this.form.get('email')?.value;

    if (!email || !this.form.get('email')?.valid) {
      const toast = await this.toastCtrl.create({
        message: 'Por favor ingresa un email válido',
        duration: 3000,
        color: 'danger',
        buttons: [
          {
            icon: 'close',
          },
        ],
      });
      await toast.present();
      return;
    }

    this.isSendingCode = true;
    const loading = await this.loadingCtrl.create({
      message: 'Enviando código de verificación...',
    });
    await loading.present();

    try {
      // Si ya se había enviado un código anteriormente, resetear el estado de verificación
      if (this.verificationCodeSent) {
        this.verificationCodeVerified = false;
        this.form.get('verificationCode')?.setValue('');
        this.form.get('verificationCode')?.setErrors(null);
      }

      // Generar código de verificación
      this.verificationCode = this.generateVerificationCode();

      // Obtener nombre del formulario o usar un valor por defecto
      const name = this.form.get('name')?.value || 'Usuario';

      // Preparar datos para el template
      const dynamicTemplateData = {
        name: name,
        code: this.verificationCode,
      };

      // Enviar email usando el endpoint sin autenticación
      const url = environment.api.mailSend.urlWithoutAuth;
      // Usar HttpClient directamente sin headers de autenticación
      const response = await firstValueFrom(
        this.http.post(
          url,
          {
            email: email,
            templateId: 'd-0fcf2c07ddd44bb38b62a15997f56632',
            dynamicTemplateData: dynamicTemplateData,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
      );

      await loading.dismiss();

      if (response && ((response as any).code === '200' || (response as any).success)) {
        this.verificationCodeSent = true;
        // Hacer el campo de código requerido cuando se envía el código
        this.form.get('verificationCode')?.setValidators([Validators.required]);
        this.form.get('verificationCode')?.updateValueAndValidity();

        // Iniciar el contador de reenvío
        this.startResendCountdown();

        const toast = await this.toastCtrl.create({
          message: 'Código de verificación enviado a tu correo',
          duration: 3000,
          color: 'success',
          buttons: [
            {
              icon: 'close',
            },
          ],
        });
        await toast.present();
        log.info('Código de verificación enviado', { email, code: this.verificationCode });
      } else {
        throw new Error('Error al enviar el código de verificación');
      }
    } catch (error) {
      await loading.dismiss();
      log.error('Error enviando código de verificación', error);
      const toast = await this.toastCtrl.create({
        message: 'Error al enviar el código de verificación. Por favor intenta nuevamente.',
        duration: 3000,
        color: 'danger',
        buttons: [
          {
            icon: 'close',
          },
        ],
      });
      await toast.present();
    } finally {
      this.isSendingCode = false;
    }
  }

  // Validar código de verificación
  validateVerificationCode() {
    const enteredCode = this.form.get('verificationCode')?.value;

    if (!enteredCode) {
      this.verificationCodeVerified = false;
      return;
    }

    if (enteredCode === this.verificationCode) {
      this.verificationCodeVerified = true;
      this.form.get('verificationCode')?.setErrors(null);
    } else {
      this.verificationCodeVerified = false;
      this.form.get('verificationCode')?.setErrors({ incorrectCode: true });
    }
  }

  /**
   * Inicia el contador para el botón de reenvío
   */
  private startResendCountdown(): void {
    // Limpiar el timer anterior si existe
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    // Iniciar el contador desde el tiempo de espera configurado
    this.resendCountdown = this.RESEND_COOLDOWN_SECONDS;

    // Actualizar el contador cada segundo
    this.resendTimer = setInterval(() => {
      this.resendCountdown--;

      // Cuando el contador llegue a 0, limpiar el intervalo
      if (this.resendCountdown <= 0) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
        this.resendCountdown = 0;
      }
    }, 1000);
  }

  /**
   * Detiene el contador de reenvío y limpia el intervalo
   */
  private stopResendCountdown(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.resendCountdown = 0;
  }

  /**
   * Formatea el tiempo restante en formato MM:SS
   */
  getFormattedCountdown(): string {
    const minutes = Math.floor(this.resendCountdown / 60);
    const seconds = this.resendCountdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Verifica si se puede reenviar el código (contador en 0)
   */
  get canResendCode(): boolean {
    return this.resendCountdown === 0 && this.verificationCodeSent;
  }

  ngOnDestroy(): void {
    // Limpiar el contador al destruir el componente
    this.stopResendCountdown();
  }

  // Obtener campos faltantes con nombres amigables
  getMissingFields(): string[] {
    const missingFields: string[] = [];
    const fieldNames: { [key: string]: string } = {
      name: 'Nombre',
      lastName: 'Apellido paterno',
      slastName: 'Apellido materno',
      email: 'Email',
      password: 'Contraseña',
      dateOfBirth: 'Fecha de nacimiento',
      gender: 'Género',
      termsAccepted: 'Términos y condiciones',
      verificationCode: 'Código de verificación',
    };

    Object.keys(fieldNames).forEach((key) => {
      const control = this.form.get(key);
      if (control && control.invalid && (control.touched || this.showFieldErrors)) {
        missingFields.push(fieldNames[key]);
      }
    });

    // Validaciones adicionales
    if (!this.isAdult && this.form.get('dateOfBirth')?.value && this.isValidDate) {
      if (!missingFields.includes('Fecha de nacimiento')) {
        missingFields.push('Debes ser mayor de 18 años');
      }
    }

    if (!this.verificationCodeVerified && this.verificationCodeSent) {
      if (!missingFields.includes('Código de verificación')) {
        missingFields.push('Código de verificación');
      }
    }

    return missingFields;
  }

  // Verificar si un campo tiene error
  hasFieldError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    if (!control) return false;
    return control.invalid && (control.touched || this.showFieldErrors);
  }

  async onSubmit() {
    // Marcar todos los campos como touched para mostrar errores
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
    this.showFieldErrors = true;

    // Validar código de verificación antes de continuar
    if (!this.verificationCodeVerified) {
      const missingFields = this.getMissingFields();
      this.missingFieldsMessage =
        missingFields.length > 0
          ? `Faltan los siguientes campos: ${missingFields.join(', ')}`
          : 'Por favor verifica tu correo electrónico antes de continuar';

      const toast = await this.toastCtrl.create({
        message: this.missingFieldsMessage,
        duration: 3000,
        color: 'danger',
        buttons: [
          {
            icon: 'close',
          },
        ],
      });
      await toast.present();
      return;
    }

    // Verificar si hay campos faltantes o si no es mayor de edad
    if (!this.isAdult && this.form.get('dateOfBirth')?.value && this.isValidDate) {
      const toast = await this.toastCtrl.create({
        message: 'Debes ser mayor de 18 años para registrarte',
        duration: 3000,
        color: 'danger',
        buttons: [
          {
            icon: 'close',
          },
        ],
      });
      await toast.present();
      return;
    }

    const missingFields = this.getMissingFields();
    if (missingFields.length > 0) {
      this.missingFieldsMessage = `Faltan los siguientes campos: ${missingFields.join(', ')}`;
      const toast = await this.toastCtrl.create({
        message: this.missingFieldsMessage,
        duration: 3000,
        color: 'danger',
        buttons: [
          {
            icon: 'close',
          },
        ],
      });
      await toast.present();
      return;
    }

    this.isLoading = true;

    try {
      if (this.form.valid) {
        const { name, lastName, slastName, email, password, gender, dateOfBirth: dateOfBirthInput } = this.form.value;

        // Convertir fecha de DD-MM-AAAA a AAAA-MM-DD para Firebase
        let dateOfBirth = dateOfBirthInput;
        if (dateOfBirthInput && dateOfBirthInput.includes('-')) {
          const parts = dateOfBirthInput.trim().split('-');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            dateOfBirth = `${year}-${month}-${day}`;
          }
        }
        await this.customerS.getBlackUsers().then((blackUsers) => {
          this.blackUser = blackUsers || [];
        });
        const isBlackUser = this.blackUser.some((user) => user.blackEmail === email);
        if (isBlackUser) {
          this.customAlert('Error', 'No es posible registrarse mediante este correo eléctronico.');
          this.isLoading = false;
          return;
        }

        const response = await this.authS.signUpEmail(
          email,
          password,
          {
            phoneNumber: '',
            email,
            photoUrl: '',
            enable: true,
            code: '',
            isActive: true,
            verified: false,
            idVerified: false,
            idChecking: false,
            phoneVerified: false,
            emailVerified: true,
            firstName: name,
            lastName: lastName,
            slastName: slastName,
            createdAt: Timestamp.now(),
            gender: gender,
            dateOfBirth: dateOfBirth,

            idRejected: false,
            documentRejected: false,
            documentVerified: false,
          },
          {
            displayName: name,
            phoneNumber: '',
            email,
            photoUrl: '',
            enable: true,
            createdAt: serverTimestamp() as any,
            modifiedAt: serverTimestamp() as any,
            lastLogin: serverTimestamp() as any,
            actualLogin: serverTimestamp() as any,
          }
        );

        if (response?.data?.uid) {
          this.trackCreateUser(response);
        }

        if (response && response.ok && response.data) {
          // const alert = await this.alertCtrl.create({
          //   header: 'Usuario registrado correctamente',
          //   buttons: ['Ok'],
          // });
          // await alert.present();
          const login = await this.authS.logInEmail(email, password, true);

          if (login) {
            // Crear wallet automáticamente después del registro exitoso
            await this.createInitialWallet();

            const token = localStorage.getItem('push-notification-token');
            if (token) {
              await this.notificationsS.savePushTokenToFirestore(token);
            }

            // Si está en modal, cerrar el modal y navegar
            if (this.isModal) {
              await this.modalCtrl.dismiss({ success: true });
              this.navCtrl.navigateForward(
                [
                  '/home',
                  {
                    queryParamsHandling: 'preserve',
                  },
                ],
                {
                  replaceUrl: true,
                }
              );
            } else {
              this.navCtrl.navigateForward(
                [
                  '/home',
                  {
                    queryParamsHandling: 'preserve',
                  },
                ],
                {
                  replaceUrl: true,
                }
              );
            }

            let bodyMail = {
              name: name || 'Cliente',
            };

            const send = await this.mailSrv.sendMail('d-20ba4ad77fd64ca392b0c8bcf87bbb9e', bodyMail, '');
            log.info('bodyMail', bodyMail, send);
          } else {
            if (this.isModal) {
              await this.modalCtrl.dismiss({ openLogin: true });
            } else {
              this.navCtrl.navigateForward(['/login'], {
                replaceUrl: true,
                queryParams: {
                  redirect: '/home',
                },
              });
            }
          }
        } else {
          // Lanzar el objeto de error completo para poder extraer el código
          throw { message: response?.message ?? 'Error al registrar usuario', code: response?.code };
        }
      } else {
        throw 'Formulario inválido';
      }
    } catch (error) {
      // Traducir el error de Firebase a español
      const errorMessage = this.getFirebaseErrorMessage(error);

      const toast = await this.toastCtrl.create({
        message: errorMessage,
        color: 'danger',
        duration: 3000,
        buttons: [
          {
            icon: 'close',
          },
        ],
      });

      await toast.present();
    }
    this.isLoading = false;
  }

  public async trackCreateUser(user: any): Promise<void> {
    const userEmail = user?.data?.email;
    const userName = user?.data?.firstName;

    try {
      // 1. Evento: account_created
      await this.trackingS.trackEvent(
        'account_created',
        {
          email: userEmail,
          first_name: userName,
        },
        false,
        true
      );
      //console.log('✅ account_created event tracked successfully');

      // 2. Evento: campaign_conversion si hay UTM
      const utmParams = this.campaignS.getUTMParams();
      const hasUTM = utmParams && Object.values(utmParams).some((v) => v !== null);

      if (hasUTM) {
        await this.trackingS.trackEvent(
          'campaign_conversion',
          {
            ...utmParams,
            conversion_type: 'registro',
            email: userEmail,
          },
          false,
          true
        );
        log.info('campaign_conversion event tracked (registro)');
      } else {
        log.error('No UTM params found. Skipping campaign_conversion.');
      }
    } catch (error) {
      log.error('Error tracking user/account events:', error);
    }
  }

  // Traducir mensajes de error de Firebase a español
  getFirebaseErrorMessage(error: any): string {
    // Extraer el código de error si está disponible
    let errorCode = 'unknown';

    if (error?.code) {
      // Si el error tiene una propiedad code, usarla directamente
      errorCode = error.code;
    } else if (typeof error === 'string') {
      // Si es un string, intentar extraer el código del mensaje
      const codeMatch = error.match(/auth\/[a-z-]+/);
      if (codeMatch) {
        errorCode = codeMatch[0];
      } else if (error.includes('email-already-in-use') || error.includes('email already in use')) {
        errorCode = 'auth/email-already-in-use';
      } else if (error.includes('invalid-email') || error.includes('invalid email')) {
        errorCode = 'auth/invalid-email';
      } else if (error.includes('weak-password') || error.includes('weak password')) {
        errorCode = 'auth/weak-password';
      } else if (error.includes('network') || error.includes('conexión')) {
        errorCode = 'auth/network-request-failed';
      }
    } else if (error?.message) {
      // Si tiene message, intentar extraer el código del mensaje
      const codeMatch = error.message.match(/auth\/[a-z-]+/);
      if (codeMatch) {
        errorCode = codeMatch[0];
      }
    }

    const errorMap: { [key: string]: string } = {
      'auth/email-already-in-use':
        'Este correo electrónico ya está registrado. Por favor, inicia sesión o usa otro correo.',
      'auth/invalid-email': 'El correo electrónico no es válido.',
      'auth/weak-password': 'La contraseña es muy débil. Debe tener al menos 6 caracteres.',
      'auth/operation-not-allowed': 'Esta operación no está permitida. Contacta al soporte.',
      'auth/too-many-requests': 'Demasiados intentos fallidos. Inténtalo más tarde.',
      'auth/network-request-failed': 'Error de conexión. Verifica tu red e inténtalo nuevamente.',
      'auth/internal-error': 'Ocurrió un error interno. Intenta de nuevo más tarde.',
      'auth/missing-email': 'Por favor, ingresa un correo electrónico.',
      'auth/invalid-credential': 'Las credenciales no son correctas, revisa e inténtalo de nuevo.',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada.',
      'auth/popup-closed-by-user': 'Operación cancelada.',
      'auth/requires-recent-login': 'Por seguridad, vuelve a iniciar sesión e intenta de nuevo.',
      unknown: 'Error al registrar usuario. Por favor, inténtalo de nuevo.',
    };

    return errorMap[errorCode] || errorMap['unknown'];
  }

  /**
   * Crea el wallet inicial de tipo "loteria" para el usuario recién registrado
   */
  private async createInitialWallet(): Promise<void> {
    try {
      // Obtener todos los tipos de wallet
      const walletTypes = await this.walletTypeS.getAll();
      const loteria = walletTypes.find((w) => w.walletName === 'loteria');

      if (loteria) {
        // Crear el wallet de tipo loteria con moneda MXN
        const walletCreated = await this.walletApiS.createWallet({
          walletTypeId: loteria.uid!,
          currency: 'MXN',
        });

        if (walletCreated) {
          log.info('Wallet creado exitosamente durante el registro', {
            walletId: walletCreated.id,
            walletName: walletCreated.walletName,
          });
        } else {
          log.warn('No se pudo crear el wallet durante el registro, pero el proceso continúa');
        }
      } else {
        log.error('No se encontró el tipo de wallet "loteria" para crear el wallet inicial');
      }
    } catch (error) {
      // No bloquear el flujo de registro si falla la creación del wallet
      // El wallet se creará de forma lazy cuando el usuario acceda a home
      log.error('Error al crear wallet durante el registro:', error);
    }
  }

  async customAlert(header: string, message: string) {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: ['OK'],
    });

    await alert.present();
  }

  async openLegalModal(doc: string) {
    const modal = await this.modalCtrl.create({
      component: LegalComponent,
      componentProps: {
        doc,
        from: 'register',
      },
      // breakpoints: [0, 0.9],
      // initialBreakpoint: 0.9,
      backdropDismiss: true,
    });

    await modal.present();
  }

  goToLogin() {
    if (this.isModal) {
      // Cerrar el modal de registro y abrir el modal de login
      this.modalCtrl.dismiss({ openLogin: true });
    } else {
      this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
    }
  }

  close() {
    this.modalCtrl.dismiss({ success: false });
  }
}
